import { ConnectorInfo } from '@blaze-shared/api-contracts-domain';
import { BaseSchema } from '@blaze-shared/schema-domain';
import { UpdateWorkflowNode } from '@blaze-shared/workflow-domain';

import {
  DATASET_SOURCE_ID,
  ERROR_MESSAGE_SOURCE_ID,
  SUCCESS_MESSAGE_SOURCE_ID,
  VALUE_SOURCE_ID,
  WorkflowError,
} from '../../errors';
import {
  getColumnReferencesFromQueryNode,
  getNamedInputReferencesFromQueryNode,
  getParamReferencesFromQueryNode,
  getTagReferencesFromQueryNode,
} from '../../references/get-references-from-query-node';
import {
  getColumnReferencesFromNodeAssignments,
  getColumnReferencesFromString,
  getNamedInputReferencesFromString,
  getParamReferencesFromString,
  getTagReferencesFromString,
} from '../../references/get-references-from-string';
import {
  WorkflowNodeColumnReference,
  WorkflowNodeNamedInputReference,
  WorkflowNodeParamReference,
  WorkflowNodeTagReference,
} from '../../references/references.models';
import { BaseWorkflowNode, WorkflowNode } from '../../registry/node.registry';
import { validateDataset } from '../_validations/dataset';

@WorkflowNode('Update')
export class UpdateNode extends BaseWorkflowNode<UpdateWorkflowNode> {
  static readonly SUCCESS_MESSAGE_SOURCE_ID = SUCCESS_MESSAGE_SOURCE_ID;
  static readonly ERROR_MESSAGE_SOURCE_ID = ERROR_MESSAGE_SOURCE_ID;
  static readonly ERROR_DATASET_SOURCE_ID = DATASET_SOURCE_ID;
  static readonly ERROR_VALUE_SOURCE_ID = VALUE_SOURCE_ID;
  getColumnReferences(schemas: Map<string | symbol, BaseSchema>): WorkflowNodeColumnReference[] {
    return [
      ...getColumnReferencesFromString(
        this.node.successMessage,
        schemas,
        UpdateNode.SUCCESS_MESSAGE_SOURCE_ID,
      ),
      ...getColumnReferencesFromString(
        this.node.errorMessage,
        schemas,
        UpdateNode.ERROR_MESSAGE_SOURCE_ID,
      ),
      ...getColumnReferencesFromNodeAssignments(
        this.node,
        schemas,
        UpdateNode.ERROR_VALUE_SOURCE_ID,
      ),
      ...(this.node.dialogAssignments
        ?.flatMap((assignment, i) =>
          getColumnReferencesFromString(
            assignment.value,
            schemas,
            UpdateNode.ERROR_VALUE_SOURCE_ID + i,
          ),
        )
        .filter(Boolean) ?? []),
      ...getColumnReferencesFromQueryNode(this.node, schemas),
    ];
  }

  getTagReferences(schemas: Map<string | symbol, BaseSchema>): WorkflowNodeTagReference[] {
    return [
      ...getTagReferencesFromString(
        this.node.successMessage,
        schemas,
        UpdateNode.SUCCESS_MESSAGE_SOURCE_ID,
      ),
      ...getTagReferencesFromString(
        this.node.errorMessage,
        schemas,
        UpdateNode.ERROR_MESSAGE_SOURCE_ID,
      ),
      ...this.node.assignments.flatMap((assignment, i) =>
        getTagReferencesFromString(assignment.value, schemas, UpdateNode.ERROR_VALUE_SOURCE_ID + i),
      ),
      ...(this.node.dialogAssignments
        ?.flatMap((assignment, i) =>
          getTagReferencesFromString(
            assignment.value,
            schemas,
            UpdateNode.ERROR_VALUE_SOURCE_ID + i,
          ),
        )
        .filter(Boolean) ?? []),
      ...getTagReferencesFromQueryNode(this.node, schemas),
    ];
  }

  getParamReferences(
    schemas: Map<string | symbol, BaseSchema>,
    connectors: ConnectorInfo[],
  ): WorkflowNodeParamReference[] {
    return [
      ...getParamReferencesFromString(
        this.node.successMessage,
        schemas,
        connectors,
        UpdateNode.SUCCESS_MESSAGE_SOURCE_ID,
      ),
      ...getParamReferencesFromString(
        this.node.errorMessage,
        schemas,
        connectors,
        UpdateNode.ERROR_MESSAGE_SOURCE_ID,
      ),
      ...this.node.assignments.flatMap((assignment, i) =>
        getParamReferencesFromString(
          assignment.value,
          schemas,
          connectors,
          UpdateNode.ERROR_VALUE_SOURCE_ID + i,
        ),
      ),
      ...(this.node.dialogAssignments
        ?.flatMap((assignment, i) =>
          getParamReferencesFromString(
            assignment.value,
            schemas,
            connectors,
            UpdateNode.ERROR_VALUE_SOURCE_ID + i,
          ),
        )
        .filter(Boolean) ?? []),
      ...getParamReferencesFromQueryNode(this.node, schemas, connectors),
    ];
  }

  getNamedInputReferences(
    schemas: Map<string | symbol, BaseSchema>,
  ): WorkflowNodeNamedInputReference[] {
    return [
      ...getNamedInputReferencesFromString(
        this.node.successMessage,
        schemas,
        UpdateNode.SUCCESS_MESSAGE_SOURCE_ID,
      ),
      ...getNamedInputReferencesFromString(
        this.node.errorMessage,
        schemas,
        UpdateNode.ERROR_MESSAGE_SOURCE_ID,
      ),
      ...this.node.assignments.flatMap((assignment, i) =>
        getNamedInputReferencesFromString(
          assignment.value,
          schemas,
          UpdateNode.ERROR_VALUE_SOURCE_ID + i,
        ),
      ),
      ...(this.node.dialogAssignments
        ?.flatMap((assignment, i) =>
          getNamedInputReferencesFromString(
            assignment.value,
            schemas,
            UpdateNode.ERROR_VALUE_SOURCE_ID + i,
          ),
        )
        .filter(Boolean) ?? []),
      ...getNamedInputReferencesFromQueryNode(this.node, schemas),
    ];
  }

  override validate(
    schemas: Map<string | symbol, BaseSchema>,
    connectors: ConnectorInfo[],
  ): WorkflowError<any>[] {
    const errors = super.validate(schemas, connectors);
    return [
      ...errors,
      ...validateDataset(this.workflowId, this.nodeId, this.node.datasetId, schemas),
    ];
  }
}
